import { z } from 'zod'

/**
 * The paged-table envelope every Market and Tools table shares.
 *
 * Offset paging, not the cursor `Page<T>` the feeds use: these tables are
 * ordered by a column the member chose and drawn under a numbered pager, so
 * "page 4" has to mean the same rows twice in a row. A cursor cannot express
 * that. The README's cursor rule is a feed rule, and the difference is flagged
 * in the report rather than papered over here.
 *
 * `page` is 1-based because the number in the URL is the number on the button:
 * `?page=3` and the pill marked 3 can never drift apart.
 */

/** The sizes "Rows per page" offers, in the order `2650:30609` draws them. */
export const PAGE_SIZES = [10, 20, 50, 100] as const

/**
 * One size wider than the menu. 25 was offered before the design settled on
 * 10/20/50/100, so it is already sitting in bookmarks and in links members have
 * sent each other; rejecting it would turn a shared link into a thrown search
 * param on whoever opens it, which is worse than honouring a size the menu no
 * longer lists. It parses and it pages, and the first pick from the menu
 * retires it from that URL. Spread from PAGE_SIZES rather than restated, so the
 * offered set and the accepted set cannot drift apart the way they just did.
 *
 * 6 is Market depth's fixed page (the mock draws six books, three rows of two): it has no
 * "Rows per page" menu, so it is accepted here and never offered.
 */
const ACCEPTED_PAGE_SIZES = [...PAGE_SIZES, 25, 6] as const

/**
 * A closed set, not any positive integer: a hand-edited `?pageSize=500` is a
 * rejected search param rather than a request for five hundred rows the backend
 * would one day have to serve.
 */
export const pageSizeSchema = z.literal(ACCEPTED_PAGE_SIZES)

export const tableQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: pageSizeSchema.default(10),
  sortBy: z.string().min(1).optional(),
  sortDesc: z.boolean().optional(),
})

/**
 * One page of a table, as a factory rather than a generic type: the row schema
 * has to be parsed too, or a stub could hand a component rows the schema never
 * checked and the parse would pass on the envelope alone.
 */
export const tablePageSchema = <Row extends z.ZodType>(rowSchema: Row) =>
  z.object({
    rows: z.array(rowSchema),
    page: z.number().int().min(1),
    pageSize: pageSizeSchema,
    total: z.number().int().min(0),
  })

export type PageSize = z.infer<typeof pageSizeSchema>
export type TableQuery = z.infer<typeof tableQuerySchema>

/**
 * Per-endpoint extras go on a schema that EXTENDS `tablePageSchema`, never on
 * this envelope: `liveMarketPageSchema` / `week52PageSchema` /
 * `depthPageSchema` (src/schemas/prices.ts, src/schemas/depth.ts) add their
 * as-of and copy that way, and `windowedTablePageSchema` (src/schemas/broker.ts)
 * adds Broker Chirfaar's `window`. Six features share the four fields below,
 * so a field only one of them can fill belongs to that one, not to all six.
 */
export interface TablePage<Row> {
  rows: Row[]
  page: number
  pageSize: PageSize
  total: number
}

/**
 * Narrows the plain `number` a pager hands back to the closed `PageSize`.
 *
 * `Pagination`'s `onPageSizeChange` is typed `(size: number) => void` because
 * the control is generic, but only the offered sizes may reach a query or a
 * search param. A value outside the list keeps the size in force rather than
 * throwing — the menu cannot produce one, so this is a guard, not a code path
 * a member can reach. Written once here because it is the shared pager's own
 * problem, not each screen's; `as PageSize` at a call site asserts the same
 * thing without checking it.
 */
export const toPageSize = (next: number, fallback: PageSize): PageSize => {
  const parsed = pageSizeSchema.safeParse(next)
  return parsed.success ? parsed.data : fallback
}

/**
 * Slices a fully materialised row array the way an endpoint would slice a
 * queryset.
 *
 * `total` counts the array handed in, never the slice — a service that
 * returned `rows.length` after slicing would collapse every table to a single
 * page. A caller that filters first therefore gets a pager sized to the
 * filtered set, which is what the filter chips need. A page past the end
 * returns no rows rather than throwing: a stale URL can point at page 9, and
 * an empty table is the honest answer to that.
 */
export const sliceTablePage = <Row>(rows: readonly Row[], query: TableQuery): TablePage<Row> => {
  const start = (query.page - 1) * query.pageSize

  return {
    rows: rows.slice(start, start + query.pageSize),
    page: query.page,
    pageSize: query.pageSize,
    total: rows.length,
  }
}
